import type { WalletAdapter } from "../adapters/WalletAdapter";
import { assignName, getProfile, merchantCheck, mismatchLine, payStatus, proposeName, recordTx, resolveName } from "../api";
import { reconcile } from "../settings"; // 1.5.50 — the wallet's synced settings carry its M® card record and limit
import { emojiFor, getEmoji } from "../emoji";
import { IC_BC_WARNING } from "../broadcast"; // 1.5.21 — Alexander's warning glyph, in the alert colour
import { cachedProfile, rememberProfile } from "../profileCache";
import { addTxLog } from "../txLog";
import { ARROW_R, IC_X } from "../icons"; // v0.60.12 — straight arrow, not a chevron (arrow law)
import {
  clearCredential,
  readCredential,
  saveCredential,
} from "../credentialStore";
import { LOGO_M } from "../logo";
import { playDoneElement, primeDoneElement } from "../sound"; // 1.5.38 — M's "done" from an <audio> element
import { sendHandoff, tellMerchant, type AsCred } from "../handoff"; // 1.5.27 — M's tab → the checkout's frame; 1.5.51 ?as=; 1.5.55 → the merchant's page (m.js's button)
import { brandedQrSvg } from "../qr"; // 1.5.55 — M's QR for the phone in M's tab on a computer
import { readNick } from "../nickname";
import { formatCode } from "../codes";
import { canonicalAmount, deslugifyRef, formatUsdShort } from "../money";
import { currency, formatFx } from "../fx";
import { fxFor, needLine, planPay, payWithTopUp, sourceLine, type PayPlan } from "../payLadder"; // 1.4.58
import { readFlagIdb, readFlagSync, setFlag } from "../deviceFlag";
import { initSlideToConfirm } from "../slideToConfirm";
import { failureLine, FAIL_FUNDS, fitFailTitle, isPasskeyCancel, recordError } from "../diag"; // v0.90.22
import type { SendPrefill } from "../sendReceiveFlow";
import { cardAbove, cardFromSettings, clearPending, goToApprover, readCard, readPending, savePending, takeReturn, verifyApproval, type PendingPay } from "../card";
import type { CardCredential } from "../cardApproval"; // 1.5.42 — the M® card
import { IC_CARD, IC_KEY } from "../icons"; // 1.5.42 — Alexander's card and key glyphs


// Does THIS browser already have a live M wallet? m.hasWallet is the authority:
// set only after a successful create/login, cleared on delete (both localStorage
// and IndexedDB), and it survives a lone cache wipe. m.cred is just a silent-
// reconnect shortcut, so we don't trust it for this decision.
async function hasWalletHere(): Promise<boolean> {
  if (readFlagSync().has) return true;
  return (await readFlagIdb()).has;
}

// Pay-by-name link (v0.11) + payment requests. Resolve the recipient name →
// address (address stays hidden), take/lock an amount, authenticate, send. On
// success the form is REPLACED by a clean, centred receipt.
//
// Request links (mdrop.it/{name}/USD{amount}[/{ref}]) arrive with lockedAmount
// (+ reference): the amount is shown read-only and can't be edited. A malformed
// amount segment arrives as `malformed` → we know WHO but not HOW MUCH, so we
// degrade gracefully to the open pay form for the right recipient.

interface PayViewDeps {
  adapter: WalletAdapter;
  recipientSlug: string;
  /** "Go to my wallet" — optionally carrying this payment so it isn't lost. */
  onGoHome: (prefill?: SendPrefill) => void;
  onPaidAs: (senderAddress: string) => void;
  lockedAmount?: number;
  /** v0.80.29 — the currency the link carries ({CCY}{amount}); USD if absent. */
  lockedCurrency?: string;
  reference?: string;
  malformed?: boolean;
  /** v0.99.0 — a merchant's return link (?back=), already allow-listed by main.ts. */
  merchantBack?: { url: string; label: string };
  /** 1.5.17 — the link was scanned from M's QR on a computer's checkout: the order is on that screen. */
  scanned?: boolean;
  /** 1.5.21 — the merchant site whose checkout drew the QR (?site=), checked with the Worker, never taken on its word. */
  site?: string;
  /** 1.5.27 — opened by M® Checkout's "Pay with M® ↗" (?handoff=1): M's tab for the first payment at a merchant, in
   *  Midnight Cobalt. Paid → the frame is told (and given the wallet's public values), and the tab closes itself. */
  handoff?: boolean;
  /** 1.5.51 — the wallet a checkout's frame picked ("Choose another M® wallet") and could not take the card for: its public
   *  values (?as=), so this tab pays from it — connected with no prompt — and takes the card here. */
  as?: AsCred | null;
  /** 1.5.53 — opened by the frame's "Choose another M® wallet" (?pick=1): the passkey picker as the tab opens, then the
   *  payment from the wallet picked — with its card above its limit — and the frame keeps the wallet it knew. */
  pick?: boolean;
}

export function renderPayView(
  root: HTMLElement,
  {
    adapter,
    recipientSlug,
    onGoHome,
    onPaidAs,
    lockedAmount,
    lockedCurrency,
    reference,
    malformed,
    merchantBack,
    scanned,
    site,
    handoff,
    as: asCred,
    pick,
  }: PayViewDeps,
) {
  // 1.5.34 (Alexander, 29 Oct) — a merchant's checkout paid here (M's tab from a checkout, its QR scanned, a way back to
  // the merchant): the card is "M® CHECKOUT" and the button "Pay with M®"; a person's request keeps Paying / Pay.
  const checkout = !!handoff || !!scanned || !!merchantBack;
  const PAY_LBL = checkout ? "Pay with M<sup>®</sup>" : "Pay";
  // v0.80.29 — amounts print in the link's own currency ({CCY}{amount}).
  const fmtAmt = (n: number) =>
    lockedCurrency && lockedCurrency !== "USD" ? formatFx(lockedCurrency, n) : formatUsdShort(n);
  // 1.5.39 (Alexander, 29 Oct, from his recordings) — a checkout's tab shows M's logo alone while it loads: the merchant's
  // wallet code never shows at a checkout (the frame never shows it either).
  root.innerHTML = `
    <main class="screen pay">
      <div class="brand small">${LOGO_M}</div>
      ${checkout ? "" : `<p class="loading" id="loading">Looking up ${escapeHtml(formatCode(recipientSlug))}…</p>`}
    </main>
  `;

  resolveName(recipientSlug).then(
    (recipient) => {
      if (malformed) renderLooksOff(recipient.display, recipient.address);
      else renderForm(recipient.display, recipient.address);
    },
    () => renderUnknown(),
  );

  function renderUnknown() {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand">${LOGO_M}</div>
        <h1>Code not found</h1>
        <p class="sub">No M wallet has the code “${escapeHtml(formatCode(recipientSlug))}”. Double-check the link.</p>
        <button id="home" class="btn ghost">Go to M</button>
      </main>
    `;
    root.querySelector("#home")!.addEventListener("click", () => onGoHome());
  }

  // Name resolved, but the amount in the link was broken. We know who to pay,
  // not how much — offer the open pay form rather than trapping the payer.
  function renderLooksOff(display: string, address: string) {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand small">${LOGO_M}</div>
        <h1>This request link looks off</h1>
        <p class="sub">The amount didn't come through. You can still pay
          <b class="cap">${escapeHtml(display)}</b> — just enter the amount yourself.</p>
        <button id="payAnyway" class="btn primary block">Pay <span class="cap">${escapeHtml(display)}</span></button>
        <button id="home" class="btn ghost block">Go to my wallet</button>
      </main>
    `;
    root
      .querySelector("#payAnyway")!
      .addEventListener("click", () => renderForm(display, address));
    root
      .querySelector("#home")!
      .addEventListener("click", () =>
        onGoHome({ toAddress: address, toDisplay: display }),
      );
  }

  function renderForm(display: string, toAddress: string) {
    const locked = lockedAmount != null && lockedAmount > 0;
    const curSym = lockedCurrency && lockedCurrency !== "USD" ? currency(lockedCurrency).symbol : "$";
    // v1.1.24 — the link's own reference, or (when it has none) the payer's
    // optional note, typed on the card (Alexander, 4 Oct 21:40).
    const linkRef = reference ? deslugifyRef(reference) : "";
    let refDisplay = linkRef;
    // v0.70.10 — same pass-style card + slider as the Send screen. The recipient
    // shows as their profile (name + emoji) rather than a code.
    // v0.99.8 — a merchant checkout can be left before paying: ✕ returns to it.
    // 1.5.55 (Alexander, 1 Nov: the frame retired — m.js draws a plain Pay with M® button that opens this tab) — on a computer
    // M's tab carries what the frame had: M's QR for the phone above the button (Apple Pay style). The phone pays on the pay
    // page; this tab learns of it from the Worker (/pay/status) and says Paid, tells the page, and goes back.
    const onComputer = !(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
    const qrHere = !!handoff && onComputer && locked && !!reference;
    const scanUrl = `${location.origin}${location.pathname}?qr=1${site ? `&site=${encodeURIComponent(site)}` : ""}`;
    // 1.5.56 (Alexander's recordings of 1.5.55, 1 Nov) — at a checkout the merchant's code never shows, not even for the half
    // second before M's records answer (Incognito: no remembered name; the return from the approver redraws the page): the
    // name as this browser last saw it, else nothing until the name comes. A person's link keeps the code until then.
    const toFirst = (() => {
      if (!checkout) return `<span class="cap">${escapeHtml(display)}</span>`;
      const c = cachedProfile(toAddress);
      return c?.name ? `<span class="cap">${escapeHtml(c.name)}</span> ${emojiFor(c.code ?? display, c.emoji)}` : "";
    })();
    root.innerHTML = `
      <main class="screen pay drop-release paylink${checkout ? " chk" : ""}${qrHere ? " pt-pc" : ""}">
        ${merchantBack ? `<a class="do-close" href="${escapeHtml(merchantBack.url)}" aria-label="Back to ${escapeHtml(merchantBack.label)}">✕</a>` : ""}
        <div class="brand small">${LOGO_M}</div>
        <div class="paycard">
          <div class="pc-title pc-chk">${checkout ? "Checkout" : "Paying"}</div>
          <div class="pc-row"><span class="pc-k">Amount:</span>${
            locked
              ? `<b class="pc-amt">${escapeHtml(fmtAmt(lockedAmount!))}</b>`
              : `<span class="pc-amt-open"><b class="pc-amt pc-cur" aria-hidden="true">${escapeHtml(curSym)}</b><input id="amt" class="pc-in pc-amt-in pc-amt" inputmode="decimal" placeholder="0" aria-label="Amount" size="2" autofocus /></span>`
          }</div>
          ${linkRef
            ? `<div class="pc-row"><span class="pc-k">Ref:</span><span class="pc-v">${escapeHtml(linkRef)}</span></div>`
            : `<label class="pc-row"><span class="pc-k">Ref:</span><input id="payRef" class="pc-in" maxlength="40" autocomplete="off" enterkeyhint="done" placeholder="Add a note (optional)" aria-label="Reference (optional)" /></label>`}
          <div class="pc-row"><span class="pc-k">To:</span><span class="pc-v" id="payTo">${toFirst}</span></div>
          <!-- 1.4.58 — rung 3 on the pay page: a shortfall in the request's currency converted from another pocket
               inside the payment (the Sending card's row, v0.90.19). Shown once the payer's pockets are read. -->
          <!-- 1.5.21 — the site's checkout isn't paid into this wallet: the name in the alert colour and this line, centred -->
          <div class="pc-warn m-warn-line" id="pcWarnRow" role="alert" hidden><span id="pcWarn"></span></div>
          <div class="pc-row pc-row-src" id="pcSrcRow" hidden><span class="pc-k">Source:</span><span class="pc-v" id="pcSrc"></span></div>
        </div>
        ${qrHere ? `<div class="em-qr pt-qr" id="ptQr"><div class="em-qr-box">${brandedQrSvg(scanUrl, 4, 2)}</div><div class="em-qr-txt"><b>Scan with your phone<br>to pay with M<sup>®</sup></b><span class="muted" id="ptQrWait">Waiting for your phone…</span></div></div><p class="em-or pt-or" id="ptOr"><span>or</span></p>` : ""}
        <!-- 1.5.36 (Alexander, 29 Oct) — one line above the button, always there, so the button never moves: what is
             happening (cyan) or what needs doing (the alert colour). At rest: Better than cash. -->
        <p class="pay-msg" id="payMsg" role="status" aria-live="polite">Better than cash.</p>
        <div class="pay-row">
          <button class="m-sq-x" id="payX" type="button" aria-label="${merchantBack ? `Back to ${escapeHtml(merchantBack.label)}` : "Close"}">${IC_X}</button>
          <div class="slide" id="paySlide" role="button" aria-label="${checkout ? "Pay with M®" : "Pay"}">
            <div class="fill"></div>
            <div class="lbl">${PAY_LBL}</div>
            <div class="ghost">${ARROW_R}</div>
            <div class="thumb">${ARROW_R}</div>
          </div>
        </div>
        <div class="pay-links">
          <button id="home" class="linkish small pay-me" type="button" aria-label="Go to my wallet">Go to my wallet</button>
          <button id="payOther" class="linkish small payother" type="button">Use another M® wallet</button>
        </div>
        <div id="payOverlay" class="drop-overlay" hidden></div>
      </main>
    `;
    // 1.5.38 — M's "done" is an <audio> element loaded inside the person's first tap on this page (sound.ts): WebKit lets that
    // element play seconds later, after the Face ID sheet, which the audio context didn't. Cheap and idempotent.
    const mainEl = root.querySelector<HTMLElement>("main.paylink");
    for (const ev of ["touchend", "pointerup", "click", "keydown"] as const) mainEl?.addEventListener(ev, primeDoneElement, { capture: true, passive: true });
    // Their profile: "Lucy Parker 🐞" if they've set a name, else "SVG8-VWRC 🦄".
    let whoHtml = `<span class="cap">${escapeHtml(display)}</span>`; // v0.99.0 — the success card uses it too
    let whoText = display; // 1.5.29 — the same, as plain words (1.5.30: the hand-off card's "Back to …")
    let whoEmoji = ""; // 1.5.48 — the approver's To row: "Ondas Coffee 🦋"
    const whoLabel = () => `${whoText}${whoEmoji ? " " + whoEmoji : ""}`;
    let mismatch: string | null = null; // 1.5.21 — the line when the site's checkout isn't paid into this wallet
    const whoReady = getProfile(toAddress) // 1.5.42 — the card's return waits for the name, so the success card has it
      .then((p) => {
        const el = root.querySelector<HTMLElement>("#payTo");
        if (!el) return;
        rememberProfile(toAddress, p); // 1.5.56 — the name for the next checkout's first paint
        const who = p.name || readNick(toAddress) || display; // v1.1.24 — their name, not their code, wherever there is one
        whoHtml = `<span class="cap">${escapeHtml(who)}</span> ${emojiFor(p.code ?? display, p.emoji)}`;
        whoText = who; whoEmoji = emojiFor(p.code ?? display, p.emoji);
        el.innerHTML = whoHtml;
        if (mismatch) whoHtml = `<span class="m-mismatch">${whoHtml}</span>`;
      })
      .catch(() => { const el = root.querySelector<HTMLElement>("#payTo"); if (el && !el.textContent?.trim()) el.innerHTML = whoHtml; }); // 1.5.56 — no name at all: the code, rather than nothing
    // 1.5.21 (Alexander, 26 Oct) — scanned from a merchant's checkout whose registered wallet isn't this one: the
    // name in yellow on Paying and on Paid, and one line saying so. The Worker decides from M's registry.
    if (site) {
      merchantCheck(site, recipientSlug).then((m) => {
        if (m.match !== false || !m.site) return;
        mismatch = mismatchLine(m.site.name);
        root.querySelector<HTMLElement>("#payTo")?.classList.add("m-mismatch");
        // 1.5.33 — the ✕ is in the button now: the name and the ⚠ line carry the warning (an alert-coloured ✕ on the green read poorly)
        if (!/m-mismatch/.test(whoHtml)) whoHtml = `<span class="m-mismatch">${whoHtml}</span>`;
        const row = root.querySelector<HTMLElement>("#pcWarnRow");
        const line = root.querySelector<HTMLElement>("#pcWarn");
        if (row && line) { line.innerHTML = `<span class="m-warn-ic">${IC_BC_WARNING}</span>${escapeHtml(mismatch)}`; row.hidden = false; }
      }).catch(() => { /* no answer, nothing to say */ });
    }

    // v1.4.2 (Alexander, 14 Oct) — the way to your wallet is your wallet: its own
    // name and emoji ("Alex Pinchuk 🦉", as the To row writes a person), in the
    // accent colour; *Pay from another wallet* on the line under it. A browser
    // with no wallet, or one whose name isn't known yet, keeps "Go to my wallet".
    // 1.5.54 — `me` is the wallet this page pays from: this browser's main wallet, or one picked here ("Use another M® wallet",
    // the checkout's "Choose another M® wallet"), which then stays the payer for this page — a retry pays from it too.
    let me = asCred?.address ?? readCredential()?.address ?? null; // 1.5.51 — a wallet the frame picked pays here
    /** 1.5.54 — a wallet's name and emoji as M writes them ("ATG 🦊"), from M's records as this browser last saw them; the
     *  device flag's emoji only for this browser's main wallet. Null while the name isn't known. */
    const walletLabel = (address: string): string | null => {
      const c = cachedProfile(address); const n = c?.name?.trim(); if (!n) return null;
      const main = readCredential()?.address?.toLowerCase() === address.toLowerCase();
      const flag = main ? readFlagSync().name : null;
      return `${n} ${flag ? getEmoji(flag) : emojiFor(c?.code ?? null, c?.emoji)}`;
    };
    const paintMe = (name: string | null | undefined, code: string | null | undefined, emoji: string | null | undefined) => {
      const el = root.querySelector<HTMLElement>("#home.pay-me");
      const n = name?.trim();
      if (!el || !n) return;
      const main = !!me && readCredential()?.address?.toLowerCase() === me.toLowerCase();
      const flag = main ? readFlagSync().name : null;
      el.textContent = `${n} ${flag ? getEmoji(flag) : emojiFor(code ?? null, emoji)}`;
    };
    if (me) {
      const c = cachedProfile(me);
      if (c) paintMe(c.name, c.code, c.emoji);
      const m0 = me;
      getProfile(m0)
        .then((p) => { rememberProfile(m0, p); if (me === m0) paintMe(p.name, p.code, p.emoji); learnedCard(m0, p); })
        .catch(() => {});
    }
    // 1.5.50 (Alexander, 31 Oct) — the card is the wallet's, not this browser's: a browser that never enrolled it (Chrome, a
    // Private tab, a second device) learns the card's public record and the limit from the wallet's synced settings, the
    // way it learns the theme — here at load for a bound wallet, and in connectForPay for a wallet just connected.
    const learned = new Set<string>();
    // 1.5.51 — another wallet's card (a pick here, or the frame's ?as=): kept in memory for this page, never merged into
    // this browser's settings (those are the bound wallet's; newest-wins would carry the other wallet's theme and card home)
    const otherCards = new Map<string, { card: CardCredential; above: number }>();
    function cardRecord(address: string): { card: CardCredential; above: number } | null {
      const a = address.toLowerCase();
      const o = otherCards.get(a); if (o) return o;
      const c = readCard(); return c && c.address.toLowerCase() === a ? { card: c, above: cardAbove() } : null;
    }
    const cardNeeded = (address: string, amount: number) => { const r = cardRecord(address); return !!r && amount > r.above; };
    function learnedCard(address: string, p: { settings?: unknown }): void {
      const a = address.toLowerCase(); if (learned.has(a)) return; learned.add(a);
      const bound = readCredential()?.address?.toLowerCase();
      if (bound && bound === a && !asCred) {
        const { changed } = reconcile(p.settings as string | null | undefined);
        if (changed.includes("card") || changed.includes("cardAbove")) { if (cardNow()) { restLine(); warmCard(); } }
      } else {
        const r = cardFromSettings(p.settings as string | null | undefined, address);
        if (r) { otherCards.set(a, r); if (cardNow()) { restLine(); warmCard(); } }
      }
    }
    /** 1.5.53 (Alexander's recording, 1 Nov, 41.75 s) — the card's line and button at rest when a card is learnt, unless the page
     *  is saying something else: a picked wallet's card, learnt a moment after the return from the approver, had repainted
     *  "Get your M® card ready" / Pay over "No card tap. Nothing was paid." / Try again */
    function restLine(): void { if (msgEl.classList.contains("alert")) return; say(null); setAddMode(addMode); }
    async function learnCard(address: string): Promise<void> {
      if (learned.has(address.toLowerCase())) return;
      try { const p = await Promise.race([getProfile(address), new Promise<null>((r) => setTimeout(() => r(null), 1500))]); if (p) { rememberProfile(address, p); learnedCard(address, p); } } catch { /* the wallet's copy is a convenience; the local record stands */ }
    }

    // 1.4.58 — the ladder: what paying this takes from this payer's pockets, read before the slide so the card
    // can say "Source: $0.63 converted" (or that nothing covers it). Re-planned at the slide for another wallet.
    const payCcy = (lockedCurrency || "USD").toUpperCase();
    let plan: PayPlan | null = null;
    let planned: { address: string; amount: number } | null = null;
    // 1.5.68 — the read in flight is shared: a pick starts it beside the card record's read, and the pay path's own ask for the
    // same wallet and amount waits for that answer (it had answered "ok" at once while the first read was still out)
    let planP: Promise<"ok" | "short"> | null = null;
    function planFor(address: string, amount: number): Promise<"ok" | "short"> {
      if (planned && planned.address === address && planned.amount === amount && planP) return planP;
      const p = planRead(address, amount);
      planP = p;
      p.catch(() => { if (planP === p) { planP = null; planned = null; } });
      return p;
    }
    async function planRead(address: string, amount: number): Promise<"ok" | "short"> {
      planned = { address, amount };
      plan = null;
      if (short) { short = false; shortFor = null; setAddMode(false); } // 1.5.31 — another wallet, read afresh (1.5.36: the line is left to what the read finds, no flash of the resting line)
      const r = await planPay(fxFor(adapter, address), address, payCcy, amount);
      if (planned.address !== address || planned.amount !== amount) return "ok"; // changed under us
      const row = root.querySelector<HTMLElement>("#pcSrcRow");
      if (r.kind === "plan") { plan = r.plan; if (row) { root.querySelector("#pcSrc")!.textContent = sourceLine(payCcy, r.plan); row.hidden = false; } return "ok"; }
      if (row) row.hidden = true;
      if (r.kind === "short") { short = true; paintShort(address, r.need); return "short"; }
      return "ok";
    }
    let short = false;
    // 1.4.88 (Alexander, 23 Oct) — short in this browser's own wallet: say so plainly, rest the slider (dimmed),
    // and make the phrase the way out — Add money, the home's sheet (/add; Back returns here, re-planned).
    // 1.5.31 (Alexander, 28 Oct, from his recording of an empty wallet picked with "Use another M® wallet": the muted
    // "Nothing in your pockets covers $0.35." under the links) — one warning line for every shortfall, under the slider
    // and above "Go to my wallet": the alert glyph, "Insufficient funds." in the alert colour, "Add money?" in M's
    // green. Add money opens the wallet that is short: another wallet's credential (from the picker) becomes this
    // browser's first — as a login with "Use another wallet" does — or /add would top up the wallet bound here.
    // 1.5.33 (Alexander, 28 Oct: "that message and saying Add money on the button — for the M tab too instead of
    // Insufficient funds") — "You need $0.35 more." in the alert colour above the button, and the button is Add money.
    const slideEl = root.querySelector<HTMLElement>("#paySlide")!;
    const msgEl = root.querySelector<HTMLElement>("#payMsg")!;
    let shortFor: string | null = null;
    let addMode = false;
    /** 1.5.36 — a first-time payer: the button is "Create wallet & pay" (was a second button under the links). */
    let createFor: string | null = null;
    // 1.5.42 (Alexander, 30 Oct) — the M® card: above $X the line says "Get your M® card ready" (the glyph, in cyan) and the
    // button "Next" — or "Get your M® key ready" / "Insert your M® key" for a key (claude/multisig-card.md §9b, the mock-ups).
    const cardFor = (amt: number | null) => (amt !== null && me && cardNeeded(me, amt) ? cardRecord(me)!.card : null); // 1.5.51 — the bound wallet's card, or a picked wallet's
    const cardNow = () => cardFor(locked ? lockedAmount! : Number((root.querySelector<HTMLInputElement>("#amt")?.value ?? "").trim()) || null);
    // 1.5.47 (Alexander, 31 Oct) — Apple Pay's order: the button says what is paid, Face ID follows the tap at once, then
    // the card on the approver; "Next" and "Insert your M® key" (the step's words) moved to the approver's line.
    const restLabel = () => (addMode ? "Add money" : createFor ? "Create wallet &amp; pay" : cardNow() ? `Pay ${cardAmt()}` : PAY_LBL);
    const cardAmt = () => fmtAmt(locked ? lockedAmount! : Number((root.querySelector<HTMLInputElement>("#amt")?.value ?? "").trim()) || 0);
    const cardLine = (kind: "card" | "key") => `<span class="card-glyph">${kind === "key" ? IC_KEY : IC_CARD}</span><span>Get your M<sup>®</sup> ${kind} ready</span>`;
    function setAddMode(on: boolean) {
      addMode = on;
      slideEl.classList.toggle("add-mode", on);
      slideEl.setAttribute("aria-label", on ? "Add money" : createFor ? "Create wallet & pay" : checkout ? "Pay with M®" : "Pay");
      const l = slideEl.querySelector<HTMLElement>(".lbl");
      if (l && !slideEl.classList.contains("done")) l.innerHTML = restLabel();
    }
    // 1.5.36 (Alexander, 29 Oct) — the line above the button: Better than cash. at rest, what is happening in cyan, what
    // needs doing in the alert colour. Everything the page says is said here (the line under the links is gone).
    const AT_REST = "Better than cash.";
    const NEW_HERE = "New to M? Create a wallet.";
    function say(text: string | null, alert = false) {
      // 1.5.52 — while a payment is under way (the wave on the button) nothing repaints the resting line: back from the
      // approver, a late profile read painted "Get your M® card ready" over the moving button for half a second
      if (text === null && slideEl.classList.contains("wave")) return;
      const card = text === null && !createFor ? cardNow() : null;
      msgEl.classList.toggle("card-line", !!card);
      if (card) msgEl.innerHTML = cardLine(card.kind); // 1.5.42
      else msgEl.textContent = text ?? (createFor ? NEW_HERE : AT_REST);
      msgEl.classList.toggle("alert", text !== null && alert);
    }
    /** 1.5.36 — the button's stages: at rest; connecting the wallet (subdued, a lit key's material); confirming the payment
     *  (the action green). Each says its line above it. 1.5.37 (Alexander, 29 Oct) — both carry the sound wave in place of
     *  words (connecting said "Paying with M®"), in the button's own ink. */
    function stage(s: "rest" | "connecting" | "confirming", line?: string) {
      slideEl.classList.toggle("connecting", s === "connecting");
      slideEl.classList.toggle("wave", s !== "rest");
      const l = slideEl.querySelector<HTMLElement>(".lbl");
      if (!l) return;
      l.style.opacity = "1";
      if (s === "rest") { l.innerHTML = restLabel(); return; }
      const said = line ?? (s === "connecting" ? "Connecting wallet…" : "Confirming payment…");
      l.textContent = said; // not drawn (the wave is); read out
      say(said);
    }
    const NOTHING_PAID = "Nothing was paid. Tap again.";
    function addMoney() {
      const c = picked;
      if (shortFor && c?.address && c.address.toLowerCase() === shortFor && readCredential()?.address?.toLowerCase() !== shortFor) saveCredential(c);
      location.assign("/add");
    }
    function paintShort(address: string, need: number) {
      shortFor = address.toLowerCase();
      say(needLine(payCcy, need), true);
      setAddMode(true);
    }
    function clearShort() {
      if (!short) return;
      short = false; planned = null; shortFor = null;
      setAddMode(false);
      say(null);
    }
    // Back from Add money (the page restored, or this tab shown again): the pockets read afresh.
    const recheck = () => { if (!slideEl.isConnected || !short || !me || !locked) return; clearShort(); void planFor(me, lockedAmount!); };
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") recheck(); });
    window.addEventListener("pageshow", (e) => { if (e.persisted) recheck(); });
    if (me && locked) void planFor(me, lockedAmount!);

    const amtEl = root.querySelector<HTMLInputElement>("#amt");
    const refEl = root.querySelector<HTMLInputElement>("#payRef");
    // v1.1.25 — the open amount keeps its symbol while typing, in the locked
    // amount's style; the field grows with the figure.
    amtEl?.addEventListener("input", () => { amtEl.size = Math.max(2, amtEl.value.length + 1); clearShort(); if (msgEl.classList.contains("alert")) say(null); }); // 1.4.88 — a new amount, a new plan (1.5.36: and the line back at rest)
    refEl?.addEventListener("input", () => { refDisplay = refEl.value.replace(/\s+/g, " ").trim().slice(0, 40); });
    refEl?.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.isComposing) { e.preventDefault(); refEl.blur(); } });
    const payOtherBtn = root.querySelector<HTMLButtonElement>("#payOther")!;
    // 1.5.56 (Alexander's Incognito recording of 1.5.55) — at a checkout in a browser with no wallet, "Go to my wallet" and
    // "Use another M® wallet" have nothing to stand for: hidden until a wallet is known here (Pay with M® shows the passkey list)
    const syncLinks = () => {
      if (!checkout) return;
      const none = !readCredential() && !sticky && !asCred;
      for (const el of [root.querySelector<HTMLElement>("#home.pay-me"), payOtherBtn]) if (el) el.style.display = none ? "none" : "";
    };

    // "Go to my wallet" carries the payment into the home Send flow so nothing
    // is lost: recipient always, plus the amount (locked, or whatever's typed)
    // and the reference.
    function currentPrefill(): SendPrefill {
      const n = locked
        ? lockedAmount
        : Number((amtEl?.value ?? "").trim()) || undefined;
      return {
        toAddress,
        toDisplay: display,
        amount: n && n > 0 ? n : undefined,
        reference: refDisplay || undefined,
      };
    }
    root
      .querySelector("#home")!
      .addEventListener("click", () => onGoHome(currentPrefill()));
    // 1.5.21 (Alexander, 26 Oct) — the muted ✕ in a squircle beside the slider, as in the app: leave without paying —
    // back to the merchant when the link names one, else to the wallet (the payment kept, as Go to my wallet does).
    // 1.5.24 — scanned from a merchant's checkout: to the wallet *without* the order (it is the merchant's; carried into
    // Send it could be paid after the merchant had cancelled it).
    root.querySelector("#payX")?.addEventListener("click", () => {
      if (handoff) { closeTab(); return; } // 1.5.27 — M's tab for a checkout: back to the merchant's tab
      if (merchantBack) location.assign(merchantBack.url);
      else if (scanned) onGoHome();
      else onGoHome(currentPrefill());
    });

    // Amount to send, as a plain decimal USDC string, or null if invalid.
    function resolveAmount(): string | null {
      if (locked) return canonicalAmount(lockedAmount!);
      const v = (amtEl?.value ?? "").trim();
      return Number(v) > 0 ? v : null;
    }

    // Connect the payer's wallet ready to sign.
    //  - default (usePicker=false): rebuild from the cached credential → NO
    //    biometric here, so the single Face ID is the signature in doTransfer().
    //    Falls back to the login ceremony (and re-caches) if there's no cache.
    //  - usePicker=true: force the iOS system passkey picker (a Face ID) so the
    //    payer can choose a different wallet — the "Pay from another wallet"
    //    escape hatch. Does NOT overwrite the cached default wallet.
    /** 1.5.31 — the wallet the picker chose (its public values), for "Add money?" when that wallet is short. */
    let picked: { id: string; publicKey: string; rpId?: string; address: string } | null = null;
    /** 1.5.54 — the wallet picked on this page: it pays a retry too, with no picker again. */
    let sticky: { id: string; publicKey: string; rpId?: string; address: string } | null = null;
    syncLinks(); // 1.5.56 — after `sticky` exists
    async function connectForPay(usePicker: boolean): Promise<{ address: string }> {
      if (!usePicker && sticky) return await adapter.connectWithCredential(sticky); // 1.5.54 — the wallet picked here, no prompt
      if (!usePicker && asCred) return await adapter.connectWithCredential(asCred); // 1.5.51 — the frame's pick, no prompt
      if (!usePicker) {
        const cached = readCredential();
        if (cached) {
          try {
            return await adapter.connectWithCredential(cached);
          } catch (e) {
            console.warn("[M] cached connect failed; using login", e);
            clearCredential();
          }
        }
      }
      const r = await adapter.createOrConnect("login");
      // 1.5.56 (Alexander's recording of 1.5.55) — the wallet is chosen: the line moves on (it said "Choose your M® wallet…"
      // for the ~2 s M loads the picked wallet and prepares the payment)
      if (root.querySelector("#payMsg")?.textContent === "Choose your M® wallet…") stage("connecting", "Verifying payment details…");
      // 1.5.54 — a browser with no main wallet keeps the one chosen as its main (most people have one); with one, a pick is
      // for this payment (the question after Paid offers to make it the main)
      if (!usePicker || !readCredential()) saveCredential({ ...r.credential, address: r.address });
      else {
        picked = { ...r.credential, address: r.address };
        sticky = picked;
      }
      // 1.5.57 (Alexander's recording of 1.5.56) — the wallet chosen is named under the button, the main one a browser with no
      // wallet has just taken too (it said "Go to my wallet")
      me = r.address;
      const c = cachedProfile(r.address); if (c) paintMe(c.name, c.code, c.emoji);
      void getProfile(r.address).then((p) => { rememberProfile(r.address, p); if (me === r.address) paintMe(p.name, p.code, p.emoji); }).catch(() => {});
      // 1.5.68 (Alexander, 3 Nov: the ~2 s of "Verifying payment details…" after a pick) — the payment is prepared and the
      // pockets read while the wallet's card record is read, not after it: the record decides only where the op goes (to the
      // approver or not), never what it is. The prepare is Circle's stub only — nothing reserved; the sponsorship stays after
      // the decision (goForCard). The credential is the one goForCard will use, so its cache takes this very preparation.
      const amt = resolveAmount();
      if (amt && !addMode && !createFor) {
        void prepForCard(amt, r.address, sticky ?? asCred).catch(() => {});
        void planFor(r.address, Number(amt)).catch(() => {});
      }
      await learnCard(r.address); // 1.5.50 — a wallet connected here learns its card before the payment decides its path (still bound by 1.5 s)
      return { address: r.address };
    }

    // 1.5.30 (Alexander, 27 Oct, from the hand-off tests) — the button says what is happening once tapped; no sensor is
    // named (Touch ID, Face ID, a password: the device's business). 1.5.36: its stages and the line above it (stage(), say()).
    // 1.5.27 — M's tab closes itself (a tab opened by the checkout's link may); where a browser refuses, a quiet line says so.
    function closeTab(): void {
      try { window.close(); } catch { /* refused */ }
      window.setTimeout(() => {
        const o = overlayEl();
        if (o.hidden || o.querySelector("#doCloseTab")) return;
        o.querySelector(".do-card")?.insertAdjacentHTML("beforeend", `<div class="do-s muted" id="doCloseTab" style="margin-top:10px">You can close this tab.</div>`);
      }, 400);
    }
    /** 1.5.38 (Alexander, 29 Oct: "done" silent in M's tab on the iPhone) — M's tab closes once "done" has finished: not before
     *  the card's 1.6 s, at most 2.5 s (a sound that starts late or not at all never keeps the tab). The sound is 1.49 s, and
     *  until now the tab closed at 1.6 s whether or not it had started. */
    function closeWhenDone(sound: Promise<HTMLAudioElement | null>): void {
      const shown = Date.now();
      let gone = false;
      const go = () => { if (gone) return; gone = true; window.setTimeout(closeTab, Math.max(0, 1600 - (Date.now() - shown))); };
      window.setTimeout(go, 2500);
      void sound.then((a) => { if (!a || a.ended) go(); else a.addEventListener("ended", go, { once: true }); });
    }
    // 1.5.30 — no spinner overlay before the signature: "Paying…" stands on the slider, and the card comes at acceptance
    // (success at acceptance, v0.90.26). The overlay held "Confirming on Circle Arc" over the passkey prompt until now.
    const overlayEl = () => root.querySelector<HTMLElement>("#payOverlay")!;
    // v0.90.26 — shown at acceptance; the receipt lives in Recent transactions.
    function overlaySuccess(senderAddress: string, amount: string, byPhone = false) {
      const o = overlayEl();
      // v0.99.5 — with a merchant to return to there is no ✕: the way on is the
      // merchant's button (the boarding pass is there, the wallet is in its own tab).
      o.innerHTML = `
        ${merchantBack || handoff ? "" : `<button id="doClose" class="do-close" type="button" aria-label="Close">✕</button>`}
        <div class="do-stack">
          <div class="brand small">${LOGO_M}</div>
          <div class="do-card">
            <div class="tickbig">✓</div>
            <div class="do-h one"><span class="amt">${escapeHtml(fmtAmt(Number(amount)))}</span> paid to <span class="nm">${whoHtml}</span></div>
            ${refDisplay ? `<div class="refchip">${escapeHtml(refDisplay)}</div>` : ""}
            ${cardSigned ? `<div class="do-s card-signed">Signed by your phone and your M<sup>®</sup> ${readCard()?.kind === "key" ? "key" : "card"}.</div>` : ""}
            <div class="do-s muted">${handoff ? `${byPhone ? "Paid on your phone. " : ""}Back to ${escapeHtml(whoText)}…` : scanned ? "Payment is confirmed<br>on the screen you scanned." : "Receipt in Recent transactions."}</div>
            ${merchantBack && !scanned ? `<a id="doBackTo" class="btn primary block" style="margin-top:18px" href="${escapeHtml(merchantBack.url)}">Back to ${escapeHtml(merchantBack.label)}</a>` : ""}
          </div>
        </div>`;
      o.removeAttribute("hidden");
      const sound = playDoneElement(); // 1.5.10 — M's "done": a completion has landed (1.5.38: from the element loaded in the tap, once the card is up)
      o.querySelector("#doClose")?.addEventListener("click", () => onPaidAs(senderAddress));
      // v0.99.0 — a merchant checkout continues on the merchant's page: same
      // tab, plain navigation (never window.open).
      // 1.5.27 — M's tab for a checkout: the confirmation for a moment, then back to the merchant's tab.
      // 1.5.54 (Alexander, 1 Nov, option B) — paid from a wallet that isn't this browser's main one: "Make ATG 🦊 your main
      // wallet here?" over the Paid card; the tab waits for the answer, then goes back.
      const payer = sticky ?? resumedCred;
      const main = readCredential()?.address?.toLowerCase();
      if (payer && main && payer.address.toLowerCase() === senderAddress.toLowerCase() && main !== payer.address.toLowerCase()) {
        void askMainWallet(payer).then(() => { if (handoff) closeWhenDone(sound); });
      } else if (handoff) closeWhenDone(sound);
    }
    /** 1.5.54 — the wallet the card's return paid from (the pending payment's), when it isn't the main one. */
    let resumedCred: { id: string; publicKey: string; rpId?: string; address: string } | null = null;
    /** 1.5.54 — "Make [wallet] your main wallet here?" Yes / No, keep [main]. Yes makes it this browser's main wallet (the
     *  login's switch, without its own question); either answer closes the question. */
    async function askMainWallet(payer: { id: string; publicKey: string; rpId?: string; address: string }): Promise<void> {
      const mainAddr = readCredential()!.address!;
      if (!cachedProfile(payer.address)?.name) {
        try { const p = await Promise.race([getProfile(payer.address), new Promise<null>((r) => setTimeout(() => r(null), 1200))]); if (p) rememberProfile(payer.address, p); } catch { /* the fallback words */ }
      }
      const pLabel = walletLabel(payer.address) ?? "this wallet";
      const mLabel = walletLabel(mainAddr) ?? "the one you have";
      return new Promise<void>((resolve) => {
        const el = document.createElement("div");
        el.className = "mw-pop-wrap";
        el.innerHTML = `<div class="mw-scrim"></div>
          <div class="mw-pop" role="dialog" aria-modal="true" aria-labelledby="mwQ">
            <p class="mw-q" id="mwQ">Make ${escapeHtml(pLabel)} your main wallet here?</p>
            <div class="mw-b"><button type="button" class="mw-btn" data-mw="yes">Yes</button><button type="button" class="mw-btn" data-mw="no">No, keep ${escapeHtml(mLabel)}</button></div>
          </div>`;
        root.appendChild(el);
        const done = (yes: boolean) => {
          if (yes) {
            saveCredential({ ...payer }, { ask: false });
            const code = cachedProfile(payer.address)?.code; if (code) setFlag(code);
          }
          (window as unknown as { __mainAnswer?: string }).__mainAnswer = yes ? "yes" : "no"; // the harness reads it
          el.remove();
          resolve();
        };
        el.querySelector('[data-mw="yes"]')!.addEventListener("click", () => done(true));
        el.querySelector('[data-mw="no"]')!.addEventListener("click", () => done(false));
      });
    }
    function overlayError(msg: string) {
      const o = overlayEl();
      // 1.4.77 — as the swap's (1.4.24): no ✕ (Back does the same; the title had run under it), the title on one line.
      o.innerHTML = `
        <div class="do-body">
          <div class="do-h pay-fail">Payment didn't go through</div>
          <div class="do-s">${escapeHtml(msg)}</div>
          <button id="doBack" class="btn ghost" type="button" style="margin-top:14px">Back</button>
          ${merchantBack ? `<a class="linkish small" style="display:block;margin-top:10px" href="${escapeHtml(merchantBack.url)}">Back to ${escapeHtml(merchantBack.label)}</a>` : ""}
        </div>`;
      o.removeAttribute("hidden");
      fitFailTitle(o);
      const back = () => {
        o.setAttribute("hidden", "");
        o.innerHTML = "";
        slider.reset();
      };
      o.querySelector("#doBack")!.addEventListener("click", back);
    }

    async function doTransfer(senderAddress: string, amount: string) {
      // 1.4.58 — the rung for THIS wallet and amount (the card planned for the cached one; "Pay from another
      // wallet" or a typed amount may differ). Nothing covers it → say so, no Face ID.
      if ((await planFor(senderAddress, Number(amount))) === "short") { slider.reset(); stage("rest"); return; }
      stage("confirming"); // 1.5.30 — the signature is asked for here; the overlay only once the payment is accepted (1.5.36: Confirming payment…, the wave)
      let topUp: import("../fx").FxTopUp | null = null;
      try {
        // v0.90.26 — success at acceptance; the receipt is followed in the background.
        let sub: Awaited<ReturnType<typeof adapter.submitTransfer>>;
        if (plan) {
          const fx = fxFor(adapter, senderAddress)!;
          const r = await payWithTopUp(fx, plan, payCcy, toAddress, amount); // approve + swap + transfer, one Face ID
          topUp = r.topUp;
          sub = await adapter.submit(r.calls);
        } else {
          sub = await adapter.submitTransfer(toAddress, amount, lockedCurrency);
        }
        afterSubmit(senderAddress, amount, sub, topUp);
      } catch (e) {
        await payFailed(e, senderAddress, amount);
      }
    }
    /** 1.5.42 — what follows an accepted userOp: the record, the hand-off's word to the frame, the success card, the
     *  receipt in the background. Shared by the plain path and the card's return. */
    function afterSubmit(senderAddress: string, amount: string, sub: Awaited<ReturnType<typeof adapter.submitTransfer>>, topUp: import("../fx").FxTopUp | null) {
      {
        const base = { address: senderAddress, userOpHash: sub.userOpHash, direction: "send" as const, amount, counterparty: toAddress, currency: lockedCurrency, kind: "pay" as const, ref: refDisplay || undefined }; // v0.99.0 — ref recorded
        recordTx(base).catch((e) => console.warn("[M] tx/record (submit) failed:", e));
        // 1.5.27 — M's tab tells the checkout's frame: paid, and — when the wallet that paid is this browser's — its public
        // values, so the frame pays in place next time. M's origin only (handoff.ts).
        const handBack = { to: recipientSlug, ref: reference ?? null, amount, currency: lockedCurrency ?? "USD" };
        if (handoff) {
          const c = readCredential();
          const k = readCard(); // 1.5.48 — the card's public record and the limit go to the frame too, so it hands off above the limit next time
          sendHandoff({ event: "paid", ...handBack, userOpHash: sub.userOpHash, cred: c && c.address?.toLowerCase() === senderAddress.toLowerCase() ? c : null,
            card: k && k.address.toLowerCase() === senderAddress.toLowerCase() ? { id: k.id, publicKey: k.publicKey, rpId: k.rpId, address: k.address, kind: k.kind, transports: k.transports } : null, cardAbove: cardAbove() });
          tellMerchant({ event: "paid", ...handBack, userOpHash: sub.userOpHash }); // 1.5.55 — the merchant's page, when it opened this tab (m.js's button)
        }
        paidHere = true; stopQrPoll(); // 1.5.55
        overlaySuccess(senderAddress, amount);
        void sub.settled
          .then(({ txHash, success }) => {
            recordTx({ ...base, txHash, success }).catch((e) => console.warn("[M] tx/record (receipt) failed:", e));
            if (!success) { recordError("pay/settle", new Error("userop_reverted " + txHash)); return; }
            if (handoff) { sendHandoff({ event: "settled", ...handBack, txHash }); tellMerchant({ event: "settled", ...handBack, txHash }); }
            addTxLog({ txHash, to: toAddress, code: formatCode(recipientSlug), amount, ref: refDisplay || undefined }); // v0.60.4
            if (topUp) fxFor(adapter, senderAddress)?.reportTopUp?.(topUp, txHash, true); // 1.4.58
          })
          .catch((e) => recordError("pay/settle", e));
      }
    }
    async function payFailed(e: unknown, senderAddress: string, amount: string) {
      {
        console.error(e);
        if (e instanceof Error && e.message === "rate_moved") { // 1.4.58 — the voucher priced the source away from the card
          const o = overlayEl(); o.setAttribute("hidden", ""); o.innerHTML = "";
          if (plan) { const el = root.querySelector("#pcSrc"); if (el) el.textContent = sourceLine(payCcy, plan); } // 1.4.60: the Worker's figure, the next slide settles at it
          slider.reset(); stage("rest"); say("The rate moved. Tap again.", true); // the card shows the new figure
          return;
        }
        recordError("pay", e); // v0.90.22 — pay-by-link failures reach Lab too
        if (isPasskeyCancel(e)) { // v0.90.22 — a cancel re-arms the slide, no card
          const o = overlayEl();
          o.setAttribute("hidden", "");
          o.innerHTML = "";
          slider.reset(); stage("rest"); say(NOTHING_PAID, true); // 1.5.36 (Alexander, 29 Oct)
          return;
        }
        // 1.5.33 — Arc refused it for the balance (the check before the tap couldn't answer): the Add money state, as
        // the checkout does — "You need $X more." above the button, the button Add money — not a failure card
        if (failureLine(e) === FAIL_FUNDS) {
          const o = overlayEl(); o.setAttribute("hidden", ""); o.innerHTML = "";
          slider.reset(); stage("rest");
          planned = null;
          let shown = (await planFor(senderAddress, Number(amount))) === "short";
          if (!shown) {
            let held: number | null = null;
            try { held = Number(await adapter.getBalance(senderAddress, payCcy)) || 0; } catch { /* unknown */ }
            const miss = held === null ? Number(amount) : Math.ceil((Number(amount) - Math.floor(held * 100 + 1e-7) / 100) * 100 - 1e-7) / 100;
            if (miss > 0) { short = true; paintShort(senderAddress, miss); shown = true; }
          }
          if (shown) return;
          // the reads say it's there and Arc said it isn't: the refusal's own card, as before
        }
        overlayError(failureLine(e)); // 1.4.77 — the cause only when the refusal names it
        stage("rest"); say(NOTHING_PAID, true); // under the card, for when Back is tapped
      }
    }

    // ---- 1.5.42 — the M® card (claude/multisig-card.md §9–9b) ------------------------------------------------------
    // Above $X: the op is prepared and sponsored here, so its hash is the hash Arc will execute; M's tab then goes to
    // the approver — a page on its own origin — with the op in the fragment, where the person reads the figures from
    // the calldata and taps the card; the approver returns the card's signature in the fragment; this tab resumes
    // the pending payment, verifies the signature over the op (the stand-in for the account's module on Arc), and only
    // then asks the passkey (Face ID) and sends. A spiked M can draw what it likes: it cannot reach the card (the
    // credential is bound to the approver's domain) and it cannot change the op without changing the hash the card signed.
    // 1.5.48 — the speed cut: the op is prepared and sponsored on the finger-down (the slider's onTouch), as the checkout's
    // frame does, so the tap goes straight to the passkey; trusted for 60 s for the same amount (the sponsorship's reuse).
    let cardReady: { amount: string; address: string; cred: string; at: number; p: Promise<import("../adapters/WalletAdapter").ExportedOp> } | null = null;
    // 1.5.51 — keyed by the wallet too: a payment from a picked wallet ("Use another M® wallet") must not reuse the bound
    // wallet's op prepared on the finger-down; the picked wallet is connected by its public values, with no prompt.
    // 1.5.58 (Alexander's recordings of 3 Nov, Chrome: ATG picked, Face ID asked for Lucy Parker's passkey) — the caches are
    // keyed by the credential as well as the address, and a credential is used only for its own wallet: the warm-up had
    // prepared the picked wallet's payment with the main wallet's credential, under the picked wallet's address.
    const credKey = (cred?: { id: string } | null) => cred?.id ?? "";
    function exportForCard(amount: string, address: string, cred?: { id: string; publicKey: string; rpId?: string; address: string } | null) {
      const a = address.toLowerCase();
      if (cardReady && cardReady.amount === amount && cardReady.address === a && cardReady.cred === credKey(cred) && Date.now() - cardReady.at < 60_000) return cardReady.p;
      const p = (async () => {
        const prepared = prepForCard(amount, address, cred);
        try { return await adapter.exportSponsored(await prepared); }
        catch (e) { if (cardPrep?.p !== prepared) throw e; cardPrep = null; return adapter.exportSponsored(await prepForCard(amount, address, cred)); } // a stale warm-up: once more, fresh
      })();
      cardReady = { amount, address: a, cred: credKey(cred), at: Date.now(), p };
      p.catch(() => { if (cardReady?.p === p) cardReady = null; });
      return p;
    }
    // 1.5.52 (Alexander's recording, 31 Oct: ~1.5 s of "Getting the payment ready…" after Pay in M's tab) — the connect and
    // the prepare (nonce, gas, Circle's stub — free, nothing reserved) kept apart from the sponsorship, and warmed as M's tab
    // opens from a checkout (it is opened to pay); the sponsorship stays on the finger-down. Trusted 4 minutes (the adapter's 5).
    let cardPrep: { amount: string; address: string; cred: string; at: number; p: Promise<import("../adapters/WalletAdapter").PreparedOp> } | null = null;
    function prepForCard(amount: string, address: string, cred?: { id: string; publicKey: string; rpId?: string; address: string } | null) {
      const a = address.toLowerCase();
      if (cred && cred.address.toLowerCase() !== a) return Promise.reject(new Error("card_other_wallet")); // 1.5.58 — never another wallet's credential
      if (cardPrep && cardPrep.amount === amount && cardPrep.address === a && cardPrep.cred === credKey(cred) && Date.now() - cardPrep.at < 240_000) return cardPrep.p;
      const p = (async () => { if (cred) await adapter.connectWithCredential(cred); else await connectForPay(false); return adapter.prepareTransfer(toAddress, amount, lockedCurrency); })();
      cardPrep = { amount, address: a, cred: credKey(cred), at: Date.now(), p };
      p.catch(() => { if (cardPrep?.p === p) cardPrep = null; });
      return p;
    }
    // 1.5.52 — a checkout's hand-off tab is opened to pay: above the limit, prepare the payment now (no prompt, nothing
    // reserved). 1.5.53 — also once the card is learnt (a picked wallet's arrives after the load), and the pockets read with it.
    const returning = /^#[cx]=/.test(location.hash);
    function warmCard(): void {
      if (!handoff || pick || returning || !me || addMode || plan || createFor || !cardNow()) return;
      const amount = resolveAmount(); const c = payerCred();
      if (!amount || !c?.address) return;
      void prepForCard(amount, me, { id: c.id, publicKey: c.publicKey, rpId: c.rpId, address: c.address }).catch(() => {}); // its public values: never a login prompt at load
      void planFor(me, Number(amount)).catch(() => {});
    }
    function touchForCard() {
      if (addMode || plan || createFor || !me || !cardNow()) return;
      const amount = resolveAmount(); if (!amount) return;
      void exportForCard(amount, me).catch(() => {});
    }
    /** 1.5.58 — the credential of the wallet this page pays from (`me`): the pick, the frame's pick, or the main wallet's only
     *  when it is the payer. Null when none is known here (a payer whose credential this page doesn't hold). */
    function payerCred(): { id: string; publicKey: string; rpId?: string; address: string } | null {
      const m = me?.toLowerCase(); if (!m) return null;
      for (const c of [sticky, asCred, readCredential()]) if (c?.address && c.address.toLowerCase() === m) return { id: c.id, publicKey: c.publicKey, rpId: c.rpId, address: c.address };
      return null;
    }
    async function goForCard(senderAddress: string, amount: string, cred?: { id: string; publicKey: string; rpId?: string; address: string } | null): Promise<void> {
      const card = cardRecord(senderAddress)!.card;
      if (plan) throw new Error("card_plan"); // the ladder's approve + swap + transfer: the approver can't read those words yet
      if (card.address && card.address.toLowerCase() !== senderAddress.toLowerCase()) throw new Error("card_other_card"); // 1.5.58
      const x = await exportForCard(amount, senderAddress, cred);
      cardReady = null; // used once
      // 1.5.58 — the op is the payer's and the passkey is the payer's: the warm-up had built the picked wallet's payment on
      // the main wallet's account, and Face ID asked for the main wallet's passkey. A mismatch is refused before any prompt.
      if (x.op.sender.toLowerCase() !== senderAddress.toLowerCase()) { cardPrep = null; throw new Error("card_other_wallet"); }
      const now = adapter.connectedAddress?.();
      if (now != null && now.toLowerCase() !== senderAddress.toLowerCase()) {
        const back = cred ? await adapter.connectWithCredential(cred) : await connectForPay(false);
        if (back.address.toLowerCase() !== senderAddress.toLowerCase()) throw new Error("card_other_wallet");
      }
      // 1.5.47 (Alexander, 31 Oct: "I tap Pay (once), I tap my card, and all is done") — Apple Pay's order: the phone signs
      // here, on the tap (Face ID at once — a system interstitial stands in front of a passkey asked for without a tap),
      // the card signs on the approver, and the return sends with no prompt. The op is sponsored and its hash final, so
      // the phone signs exactly what the card will see; above $X the phone's signature is useless without the card's,
      // and a cancel on the approver leaves nothing that executes (multisig-card.md §9b, the order reversed on purpose).
      stage("confirming", "Authorizing payment…"); // 1.5.48 — "Confirming" is the approver's word (Alexander)
      const signature = await adapter.signExported(x);
      const pending: PendingPay = { v: 1, href: location.href.replace(/#.*$/, ""), at: Date.now(), address: senderAddress as `0x${string}`, chainId: x.chainId, entryPoint: x.entryPoint, op: x.op, hash: x.hash, amount, currency: lockedCurrency, toAddress, signature, cred: cred ?? undefined, card: cred ? card : undefined };
      savePending(pending);
      // 1.5.52 (Alexander, 1 Nov) — the button stays green on "Authorizing payment…" through Face ID and the trip to the
      // approver (it fell back to the subdued "Getting the payment ready…" while Face ID's tick was still on screen)
      goToApprover({ chainId: x.chainId, entryPoint: x.entryPoint, op: x.op, hash: x.hash, cred: card.id, transports: card.transports, kind: card.kind, claim: { name: whoLabel(), currency: payCcy, from: walletLabel(senderAddress) ?? undefined, fromAddr: senderAddress } }); // 1.5.54 — the sender's name (it read "Go to my wallet" in a browser with no wallet)
    }
    const CARD_NO_TAP = "No card tap. Nothing was paid.";
    // 1.5.52 (Alexander, 1 Nov: "Verifying payment details…" — it leads into the approver's VERIFIED pills; was "Getting the payment ready…")
    const VERIFYING = "Verifying payment details…";
    // 1.5.54 (Alexander, 1 Nov) — while iOS's passkey list is up: what to do in it (was "Connecting wallet…")
    const CHOOSE = "Choose your M® wallet…";
    async function resumeFromCard(): Promise<boolean> {
      const back = takeReturn();
      const pending = readPending();
      if (!back || "card" in back) return false;
      clearPending();
      if (!pending || Date.now() - pending.at > 10 * 60_000) { if ("approval" in back) say(CARD_NO_TAP, true); return !!pending; }
      // 1.5.58 — the wallet picked here pays a retry too after the trip (it is in the tab's pending payment; the page's own
      // `sticky` was gone with the page, so the return named the main wallet again and Try again paid from it)
      if (pending.cred && pending.cred.address.toLowerCase() === pending.address.toLowerCase() && pending.cred.address.toLowerCase() !== readCredential()?.address?.toLowerCase()) {
        sticky = picked = pending.cred; me = pending.cred.address;
        if (pending.card) otherCards.set(me.toLowerCase(), { card: pending.card, above: cardRecord(me)?.above ?? 0 });
        const m1 = me; const c = cachedProfile(m1); if (c) paintMe(c.name, c.code, c.emoji);
        syncLinks();
        void learnCard(m1).then(() => { const p = cachedProfile(m1); if (p && me === m1) paintMe(p.name, p.code, p.emoji); }); // its own limit, for the retry's path; its name
      }
      const card = pending.card ?? readCard(); // 1.5.51 — a picked wallet's card rides the pending payment
      const asked = card?.id ? card.id.slice(0, 5) : "none";
      // 1.5.63 — the approver's screen timed out (3 min after this tab left, M's idle logout) and came back by itself
      if ("refused" in back && back.refused === "expired") { recordError("card/refused", new Error(`timed out · asked ${asked} · for ${pending.address.slice(0, 6)}`)); say("This took too long. Nothing was paid.", true); setTryAgain(); return true; }
      if ("refused" in back) { recordError("card/refused", new Error(`no card tap · asked ${asked} · for ${pending.address.slice(0, 6)}`)); say(CARD_NO_TAP, true); setTryAgain(); return true; }
      if (!card) { say(CARD_NO_TAP, true); return true; }
      stage("confirming", "Authorizing payment…"); // 1.5.52 — the word the trip left on, until Paid
      try {
        // 1.5.58 — the op is the pending wallet's, and so is the card: never a payment from one wallet on another's card
        if (pending.op.sender.toLowerCase() !== pending.address.toLowerCase()) throw new Error("card_other_wallet");
        if (card.address && card.address.toLowerCase() !== pending.address.toLowerCase()) throw new Error("card_other_card");
        const why = await verifyApproval(pending, back.approval, card);
        if (why) throw new Error(why);
        const sender = pending.cred ? await adapter.connectWithCredential(pending.cred) : await connectForPay(false); // 1.5.51 — the wallet that signed
        if (sender.address.toLowerCase() !== pending.address.toLowerCase()) throw new Error("card_other_wallet");
        if (pending.cred) resumedCred = pending.cred; // 1.5.54 — the question after Paid
        const x = { chainId: pending.chainId, entryPoint: pending.entryPoint, op: pending.op, hash: pending.hash };
        const sub = pending.signature ? await adapter.sendSigned(x, pending.signature) : await adapter.sendExported(x); // 1.5.47 — signed before the trip: no prompt here
        (window as unknown as { __cardSigned?: unknown }).__cardSigned = { hash: pending.hash, cred: card.id }; // the harness reads it
        cardSigned = true;
        await Promise.race([whoReady, new Promise((r) => setTimeout(r, 1500))]);
        afterSubmit(sender.address, pending.amount, sub, null);
      } catch (e) {
        if (e instanceof Error && /^card_/.test(e.message)) { recordError("card", new Error(`${e.message} · card ${asked} · for ${pending.address.slice(0, 6)}`)); stage("rest"); say(CARD_NO_TAP, true); setTryAgain(); return true; }
        await payFailed(e, pending.address, pending.amount);
      }
      return true;
    }
    let cardSigned = false; // 1.5.42 — the success card says so
    function setTryAgain() { const l = slideEl.querySelector<HTMLElement>(".lbl"); if (l) l.innerHTML = "Try again"; }

    // Brand-new payer with no wallet: offer create-and-pay (register + sign). 1.5.36 — the button itself becomes
    // "Create wallet & pay", "New to M? Create a wallet." above it (was a second button under the links).
    function offerCreateAndPay(amount: string) {
      createFor = amount;
      setAddMode(addMode); // the label and its name
      say(null);
    }
    async function createAndPay(amount: string) {
      stage("connecting", "Creating wallet…");
      try {
        let label: string | undefined;
        let slug: string | undefined;
        let display: string | undefined;
        try {
          const p = await proposeName();
          label = p.slug;
          slug = p.slug;
          display = p.display;
        } catch {
          label = `m-wallet-${Date.now().toString(36)}`;
        }
        const conn = await adapter.createOrConnect("register", label);
        saveCredential({ ...conn.credential, address: conn.address });
        setFlag(display ?? null); // this browser now holds an M wallet (code shown at login)
        if (slug) assignName(conn.address, slug).catch(() => {});
        createFor = null; setAddMode(addMode); // a wallet now: Pay from here on
        await doTransfer(conn.address, amount);
      } catch (e) {
        console.error(e);
        slider.reset(); stage("rest"); say(NOTHING_PAID, true);
      }
    }

    async function runPay(usePicker: boolean, auto = false) {
      const amount = resolveAmount();
      if (!amount) {
        slider.reset(); stage("rest"); say("Enter an amount above 0.", true);
        return;
      }
      // Whether we KNOW this browser has an M wallet decides how a failure reads:
      // a known wallet → "try again"; genuinely no wallet → offer create.
      // ("Pay from another wallet" is always a deliberate login → treat as known.)
      const known = usePicker || (await hasWalletHere());
      payOtherBtn.disabled = true;
      // the picker, or a login (no cached wallet): Connecting wallet… (the button subdued); a cached wallet: Confirming payment… at once
      // 1.5.47 — a card payment says "Getting the payment ready…" from the first frame (it had said "Confirming payment…"
      // for the moment before the card check — nothing was being confirmed yet)
      const t0 = Date.now();
      if (usePicker || (!readCredential() && !sticky && !asCred)) stage("connecting", CHOOSE); else if (!addMode && !plan && cardNow()) stage("connecting", VERIFYING); else stage("confirming");
      try {
        const sender = await connectForPay(usePicker);
        syncLinks(); // 1.5.56 — a wallet is known here now
        setFlag(null); // this browser now known to hold an M wallet (guard only)
        // 1.5.42 — above $X, with a card enrolled for this wallet: to the approver (the tab leaves; it resumes on return)
        // 1.5.51 — a wallet picked here ("Use another M® wallet") learns its card from the wallet's settings first, and takes
        // the card path like the bound one (it used to pay with the passkey alone — the hole Alexander found in Chrome)
        if (!addMode && !plan && cardNeeded(sender.address, Number(amount))) {
          if ((await planFor(sender.address, Number(amount))) === "short") { slider.reset(); stage("rest"); return; }
          stage("connecting", VERIFYING);
          await goForCard(sender.address, amount, sticky ?? asCred);
          return;
        }
        await doTransfer(sender.address, amount);
      } catch (err) {
        console.warn("[M] pay: connect/login failed", err);
        recordError("pay/run", err); // 1.5.54 — every failure here reaches Lab (the picked wallet's stop on "Verifying payment details…" left nothing)
        if (auto && Date.now() - t0 < 700) { slider.reset(); stage("rest"); say(null); return; } // 1.5.53 — the browser wanted a tap: rest, no alarm
        if (known) {
          // The wallet exists here (or the picker was cancelled) — let them retry,
          // never offer to create a second wallet.
          slider.reset(); stage("rest"); say(NOTHING_PAID, true);
        } else {
          // No wallet on this browser and the login found none → new payer path.
          slider.reset(); stage("rest"); offerCreateAndPay(amount);
        }
      } finally {
        payOtherBtn.disabled = false;
      }
    }

    // 1.5.24 (Alexander, 26 Oct) — a merchant's order (scanned from a checkout, or with a way back to the merchant): ask
    // the Worker before the slider is armed and again at the slide. Cancelled on the merchant's screen (the order was
    // changed) → "This order was cancelled.", already paid (the same QR twice) → "This order is already paid.", and
    // the slider off. A friend's request is never asked: several people may pay one link.
    const merchantOrder = !!reference && locked && (!!scanned || !!merchantBack || !!site || !!handoff);
    const orderShut = async (): Promise<boolean> => {
      if (!merchantOrder) return false;
      let st: string;
      try { st = (await payStatus(recipientSlug, reference!, canonicalAmount(lockedAmount!), lockedCurrency ?? "USD")).status; }
      catch { return false; } // no answer: the slide decides, as before
      const line = st === "cancelled" ? "This order was cancelled." : st === "paid" || st === "settled" ? "This order is already paid." : null;
      if (!line) return false;
      say(line, true); // 1.5.36 — the line above the button (was a row on the card)
      root.querySelector<HTMLElement>("#pcWarnRow")?.setAttribute("hidden", "");
      root.querySelector("#paySlide")?.classList.add("slide-short");
      return true;
    };
    void orderShut();
    // 1.5.55 — M's tab on a computer: the phone that scanned the QR pays on the pay page; the Worker says so here
    let paidHere = false;
    let qrTimer: number | undefined;
    const qrStart = Date.now();
    function stopQrPoll() { window.clearTimeout(qrTimer); qrTimer = undefined; }
    function qrPoll() {
      if (!qrHere || paidHere) return;
      qrTimer = window.setTimeout(async () => {
        if (paidHere) return;
        if (document.visibilityState === "visible") {
          try {
            const s = await payStatus(recipientSlug, reference!, canonicalAmount(lockedAmount!), payCcy);
            if (paidHere) return;
            if (s.status === "paid" || s.status === "settled") { paidByPhone(s); return; }
            const w = root.querySelector<HTMLElement>("#ptQrWait");
            if (w && s.status === "failed") w.textContent = "That payment didn't go through. Scan again to try once more.";
          } catch { /* the next tick */ }
        }
        if (Date.now() - qrStart < 15 * 60 * 1000) qrPoll();
      }, 1500);
    }
    function paidByPhone(s: { status: string; txHash?: string | null }) {
      paidHere = true; stopQrPoll();
      const amount = canonicalAmount(lockedAmount!);
      const handBack = { to: recipientSlug, ref: reference ?? null, amount, currency: lockedCurrency ?? "USD" };
      sendHandoff({ event: "paid", ...handBack, userOpHash: null, cred: null });
      tellMerchant({ event: "paid", ...handBack, userOpHash: null, viaPhone: true });
      if (s.status === "settled" && s.txHash) { sendHandoff({ event: "settled", ...handBack, txHash: s.txHash }); tellMerchant({ event: "settled", ...handBack, txHash: s.txHash, viaPhone: true }); }
      root.querySelector<HTMLElement>("#ptQr")?.setAttribute("hidden", "");
      overlaySuccess("", amount, true);
    }
    qrPoll();
    const payXEl = root.querySelector<HTMLButtonElement>("#payX");
    const slider = initSlideToConfirm(root.querySelector<HTMLElement>("#paySlide")!, {
      // 1.5.33 — Add money while nothing covers the payment; Pay otherwise
      onConfirm: () => void (async () => { if (addMode) { slider.reset(); addMoney(); return; } if (await orderShut()) { slider.reset(); return; } if (createFor) { await createAndPay(resolveAmount() ?? createFor); return; } await runPay(false); })(),
      onX: () => payXEl?.click(), // 1.5.33 — the ✕ squircle beside the slider is now the button's left segment
      onTouch: touchForCard, // 1.5.48 — the card path prepares and sponsors on the finger-down
      xLabel: payXEl?.getAttribute("aria-label") ?? "Close",
    });
    payOtherBtn.addEventListener("click", () => void runPay(true));
    if (cardNow()) { say(null); setAddMode(addMode); } // 1.5.42 — the card's line and button at rest
    void resumeFromCard(); // 1.5.42 — back from the approver: the pending payment, or its refusal
    warmCard();
    // 1.5.53 (Alexander, 1 Nov: option A) — opened by the frame's "Choose another M® wallet": the picker as the tab opens and
    // the payment from the wallet picked, as "Use another M® wallet" here does. A browser that wants a tap first refuses at
    // once (NotAllowedError in under 700 ms): the page rests, no alarm, "Use another M® wallet" ready to tap.
    if (pick && !returning) requestAnimationFrame(() => { void runPay(true, true); });
  }
}

function escapeHtml(s: string) {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}
